import { License, ManagedAccount } from '@webdev/user-management';
import { definedOrThrows } from '@webdev/utils';
import React, { FC, useCallback, useContext, useState } from 'react';
import { useSetManagedUsersToLicense } from '../user-management/';

interface Context {
  licenseId: License['id'];
  toggleModal: () => void;
  modalActive: boolean;
  selectedUsers: string[];
  setSelectedUsers: (ids: Array<ManagedAccount['id']>) => void;
  selectUser: (id: ManagedAccount['id']) => void;
  unselectUser: (id: ManagedAccount['id']) => void;
  assignLicense: () => void;
  assigningStatus: 'error' | 'idle' | 'loading' | 'success';
}
const context = React.createContext<Context | undefined>(undefined);

export const LicenseAssignmentContext: FC<{ licenseId: License['id']; openModal?: boolean }> = ({
  licenseId,
  openModal = false,
  ...props
}) => {
  const { setManagedUsersToLicense, assigningStatus } = useSetManagedUsersToLicense(licenseId);
  const [modalActive, setModalActive] = useState(openModal);
  const [selectedUsers, setSelectedUsers] = useState<Array<ManagedAccount['id']>>([]);

  const toggleModal = () => {
    setModalActive(!modalActive);
  };

  const assignLicense = () => {
    setManagedUsersToLicense(selectedUsers)
      .then(() => setModalActive(false))
      .catch(() => undefined);
  };

  const selectUser = useCallback(
    (userId: ManagedAccount['id']) => {
      setSelectedUsers((state) => [...state, userId]);
    },
    [setSelectedUsers],
  );

  const unselectUser = useCallback(
    (userId: ManagedAccount['id']) => {
      setSelectedUsers((state) => state.filter((id) => userId !== id));
    },
    [setSelectedUsers],
  );

  return (
    <context.Provider
      value={{
        selectUser,
        unselectUser,
        licenseId,
        toggleModal,
        modalActive,
        selectedUsers,
        setSelectedUsers,
        assignLicense,
        assigningStatus,
      }}
      {...props}
    />
  );
};

export const useLicenseAssignment = () =>
  definedOrThrows(useContext(context), 'LicenseAssignmentContext');
